import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { FeatureInfoComponent } from '@principle-theorem/ng-feature-flags';
import {
  ContentContainerComponent,
  CurrentPracticeScope,
  GlobalStoreService,
  InformationBoxComponent,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  BreadcrumbsComponent,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  BookingWindowType,
  IBookingWindowTiming,
  IStaffOnlineOrder,
  IStaffer,
  StaffOnlineOrderOption,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  MAX_BOOKING_WINDOW_DAYS,
  WithRef,
  filterUndefined,
  multiSwitchMap,
  snapshot,
  updateDoc,
} from '@principle-theorem/shared';
import { compact, differenceBy } from 'lodash';
import { Subject, combineLatest, of } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { OnlineBookingTimeRestrictionFormComponent } from '../online-booking-time-restriction-form/online-booking-time-restriction-form.component';

interface IStaffOrderOptionDisplay {
  value: StaffOnlineOrderOption;
  label: string;
  description: string;
}

export const STAFF_ORDER_OPTIONS: IStaffOrderOptionDisplay[] = [
  {
    value: StaffOnlineOrderOption.Alphabetically,
    label: 'Alphabetically',
    description: 'Practitioners are listed A–Z by their online profile name.',
  },
  {
    value: StaffOnlineOrderOption.Availability,
    label: 'Most Availability',
    description:
      'Practitioners with the most open appointment times are listed first.',
  },
  {
    value: StaffOnlineOrderOption.NextAvailable,
    label: 'Next Available',
    description:
      'Practitioners whose next open appointment is soonest are listed first.',
  },
  {
    value: StaffOnlineOrderOption.Custom,
    label: 'Custom',
    description: 'Drag and drop practitioners into the order you want.',
  },
];

@Component({
  selector: 'pr-patient-portal',
  imports: [
    CommonModule,
    BreadcrumbsComponent,
    FeatureInfoComponent,
    MatButtonModule,
    ContentContainerComponent,
    InformationBoxComponent,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatListModule,
    DragDropModule,
    MatSelectModule,
    MatFormFieldModule,
    OnlineBookingTimeRestrictionFormComponent,
  ],
  templateUrl: './patient-portal.component.html',
  styleUrl: './patient-portal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientPortalComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _global = inject(GlobalStoreService);
  private _currentPractice = inject(CurrentPracticeScope);
  private _organisation = inject(OrganisationService);
  private _practice = toSignal(
    this._currentPractice.doc$.pipe(filterUndefined())
  );

  staff = signal<WithRef<IStaffer>[]>([]);
  breadcrumbs = computed(() => {
    const practice = this._practice();
    return practice
      ? [
          { label: 'Settings', path: '../../../' },
          { label: practice.name },
          { label: 'Patient Portal' },
        ]
      : [];
  });

  staffOrderOptionEnum = StaffOnlineOrderOption;
  staffOrderOptions = STAFF_ORDER_OPTIONS;
  maxBookingWindowDays = MAX_BOOKING_WINDOW_DAYS;
  orderBy = new TypedFormControl(StaffOnlineOrderOption.Alphabetically);
  selectedStaffOrderLabel = signal('');
  minBookingWindow = signal<IBookingWindowTiming | undefined>(undefined);
  maxBookingWindow = signal<IBookingWindowTiming | undefined>(undefined);

  constructor() {
    this._currentPractice.doc$
      .pipe(
        filterUndefined(),
        switchMap(async (practice) => {
          const patientPortal = practice.settings?.patientPortal;
          const bookingWindow = patientPortal?.bookingWindow;
          const staffOnlineOrder = patientPortal?.staffOnlineOrder;
          const customOrder = await this._getCustomOrder(staffOnlineOrder);
          return { ...staffOnlineOrder, customOrder, bookingWindow };
        }),
        take(1),
        takeUntil(this._onDestroy$)
      )
      .subscribe(({ orderBy, customOrder, bookingWindow }) => {
        const defaultOrder = StaffOnlineOrderOption.Alphabetically;
        this.orderBy.patchValue(orderBy ?? defaultOrder, { emitEvent: false });
        this.selectedStaffOrderLabel.set(
          this._staffOrderLabel(orderBy ?? defaultOrder)
        );
        this.staff.set(customOrder);
        this.minBookingWindow.set(bookingWindow?.min);
        this.maxBookingWindow.set(bookingWindow?.max);
      });

    this.orderBy.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        this.selectedStaffOrderLabel.set(
          this._staffOrderLabel(this.orderBy.value)
        );
        void this._saveStaffOrder();
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async onMinBookingWindowChange(
    value: IBookingWindowTiming | undefined
  ): Promise<void> {
    await this.updateBookingWindow(value, BookingWindowType.Min);
  }

  async onMaxBookingWindowChange(
    value: IBookingWindowTiming | undefined
  ): Promise<void> {
    await this.updateBookingWindow(value, BookingWindowType.Max);
  }

  async updateBookingWindow(
    value: IBookingWindowTiming | undefined,
    type: BookingWindowType
  ): Promise<void> {
    const practice = await snapshot(
      this._currentPractice.doc$.pipe(filterUndefined())
    );

    await updateDoc(practice.ref, {
      settings: {
        ...practice.settings,
        patientPortal: {
          ...practice.settings?.patientPortal,
          bookingWindow: {
            ...practice.settings?.patientPortal?.bookingWindow,
            [type]: value,
          },
        },
      },
    });
  }

  async dropStaffer(event: CdkDragDrop<WithRef<IStaffer>[]>): Promise<void> {
    this.staff.update((staff) => {
      moveItemInArray(staff, event.previousIndex, event.currentIndex);
      return staff;
    });
    await this._saveStaffOrder();
  }

  private _staffOrderLabel(value: StaffOnlineOrderOption | undefined): string {
    return (
      STAFF_ORDER_OPTIONS.find((option) => option.value === value)?.label ?? ''
    );
  }

  private async _saveStaffOrder(): Promise<void> {
    const orderBy = this.orderBy.value;
    const customOrder =
      orderBy === StaffOnlineOrderOption.Custom
        ? this.staff().map((staff) => staff.ref)
        : [];
    const practice = await snapshot(
      this._currentPractice.doc$.pipe(filterUndefined())
    );
    await Firestore.patchDoc(practice.ref, {
      settings: {
        ...practice.settings,
        patientPortal: {
          ...practice.settings?.patientPortal,
          staffOnlineOrder: { orderBy, customOrder },
        },
      },
    });
  }

  private async _getCustomOrder(
    settings?: IStaffOnlineOrder
  ): Promise<WithRef<IStaffer>[]> {
    if (!settings || settings.orderBy !== StaffOnlineOrderOption.Custom) {
      return snapshot(this._organisation.practicePractitioners$);
    }

    const allPractitioners$ = this._organisation.practicePractitioners$;
    const orderedStaff$ = of(settings.customOrder).pipe(
      multiSwitchMap((ref) => this._global.getStaffer$(ref)),
      map((staff) => compact(staff))
    );

    const mergedStaff$ = combineLatest([orderedStaff$, allPractitioners$]).pipe(
      map(([orderedStaff, allPractitioners]) => {
        const newStaffers = differenceBy(
          allPractitioners,
          orderedStaff,
          (staffer) => staffer.ref.path
        );
        return [...orderedStaff, ...newStaffers];
      })
    );

    return snapshot(mergedStaff$);
  }
}
